import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, Modal, View } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useLocalSearchParams } from "expo-router";
import { doc, getDoc, collection, onSnapshot, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { createPost } from "@/lib/services";
import { useConfirm } from "@/hooks/useConfirm";
import { useAlert } from "@/hooks/useAlert";
import ConfirmSheet from "@/components/ConfirmSheet";
import AlertSheet from "@/components/AlertSheet";
import { ModalContent } from "@/components/ContentWidth";
import ContentWidth from "@/components/ContentWidth";
import { SlotListSkeleton } from "@/components/Skeleton";
import { colors, radius, font } from "@/lib/theme";

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  } catch { return dateStr; }
}

function formatTime(t: string): string {
  const h = parseInt(t.split(":")[0]);
  if (h === 12) return "12:00pm";
  return h > 12 ? `${h - 12}:00pm` : `${h}:00am`;
}

interface Booking { userId: string; displayName: string; bookedAt: any; }

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */
const Header = styled.View`padding: 20px; border-bottom-width: 1px; border-bottom-color: ${colors.line}; background-color: ${colors.white};`;
const DateTitle = styled.Text`font-size: 20px; font-weight: ${font.extrabold}; color: ${colors.ink};`;
const TimeText = styled.Text`font-size: 15px; color: ${colors.inkSecondary}; font-weight: ${font.medium}; margin-top: 4px;`;
const SpotsText = styled.Text`font-size: 14px; font-weight: ${font.bold}; color: ${colors.brand}; margin-top: 8px;`;

const Section = styled.View`padding: 20px;`;
const SectionLabel = styled.Text`font-size: 12px; font-weight: ${font.extrabold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px;`;

const BookBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.card}px; padding: 16px; align-items: center;`;
const BookBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;

const BookedBadge = styled.View`background-color: ${colors.brand50}; border: 1px solid ${colors.brandLight}; border-radius: ${radius.md}px; padding: 14px; align-items: center;`;
const BookedBadgeText = styled.Text`font-size: 15px; font-weight: ${font.bold}; color: ${colors.brandDark};`;
const CancelLink = styled.Text`font-size: 13px; color: ${colors.inkMuted}; font-weight: ${font.medium}; text-align: center; margin-top: 8px; text-decoration-line: underline;`;

const FullBadge = styled.View`background-color: ${colors.amber50}; border: 1px solid ${colors.amber200}; border-radius: ${radius.md}px; padding: 14px; align-items: center;`;
const FullBadgeText = styled.Text`font-size: 15px; font-weight: ${font.bold}; color: ${colors.amber};`;

const Card = styled.View`background-color: ${colors.white}; border-radius: ${radius.lg}px; border: 1px solid ${colors.line}; overflow: hidden;`;
const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row; align-items: center; padding: 14px 16px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;
const Avatar = styled.View`width: 36px; height: 36px; border-radius: 18px; background-color: ${colors.brandLight}; align-items: center; justify-content: center; margin-right: 12px;`;
const AvatarText = styled.Text`font-size: 13px; font-weight: ${font.extrabold}; color: ${colors.brandDark};`;
const BookingName = styled.Text`font-size: 14px; font-weight: ${font.bold}; color: ${colors.ink}; flex: 1;`;
const AdminCancelBtn = styled.Text`font-size: 12px; font-weight: ${font.bold}; color: ${colors.danger};`;

const EmptyText = styled.Text`font-size: 14px; color: ${colors.inkMuted}; text-align: center; padding: 20px;`;
const LoadingContainer = styled.View`flex: 1; justify-content: center; align-items: center; padding: 40px;`;
const Spacer = styled.View`height: 32px;`;

// Cancel reason sheet
const ReasonSheet = styled.View`background-color: ${colors.white}; border-top-left-radius: ${radius.xl}px; border-top-right-radius: ${radius.xl}px; padding: 24px; padding-bottom: 40px;`;
const ReasonTitle = styled.Text`font-size: 18px; font-weight: ${font.extrabold}; color: ${colors.ink}; text-align: center; margin-bottom: 8px;`;
const ReasonSub = styled.Text`font-size: 14px; color: ${colors.inkSecondary}; text-align: center; margin-bottom: 16px;`;
const ReasonInput = styled.TextInput`background-color: ${colors.bg}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 12px 14px; font-size: 15px; color: ${colors.ink}; min-height: 80px;`;
const ReasonBtn = styled.View<{ disabled?: boolean }>`background-color: ${(p) => p.disabled ? colors.line : colors.danger}; border-radius: ${radius.card}px; padding: 16px; align-items: center; margin-top: 16px;`;
const ReasonBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;
const ReasonCancelText = styled.Text`font-size: 15px; font-weight: ${font.bold}; color: ${colors.inkMuted}; text-align: center; margin-top: 12px;`;

export default function NetSessionScreen() {
  const { date, time } = useLocalSearchParams<{ date: string; time: string }>();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { confirmProps, confirm } = useConfirm();
  const { alertProps, alert } = useAlert();

  const [lanes, setLanes] = useState(4);
  const [bookings, setBookings] = useState<(Booking & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  // Admin cancel state
  const [cancelTarget, setCancelTarget] = useState<(Booking & { id: string }) | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const sessionId = `${date}_${time}`;
  const isAdmin = membership?.role === "admin";
  const myBooking = bookings.find((b) => b.userId === user?.uid);
  const spotsLeft = lanes - bookings.length;

  useEffect(() => {
    if (!club?.id) return;
    async function loadLanes() {
      const snap = await getDoc(doc(db, "clubs", club!.id, "settings", "nets"));
      if (snap.exists()) setLanes(snap.data().lanes || 4);
    }
    loadLanes();
  }, [club?.id]);

  useEffect(() => {
    if (!club?.id || !sessionId) return;
    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "netSessions", sessionId, "bookings"),
      (snap) => {
        setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as any);
        setLoading(false);
      }
    );
    return unsub;
  }, [club?.id, sessionId]);

  const handleBook = async () => {
    if (!club || !user || !membership) return;
    setBooking(true);
    await setDoc(doc(db, "clubs", club.id, "netSessions", sessionId, "bookings", user.uid), {
      userId: user.uid,
      displayName: membership.displayName,
      bookedAt: Timestamp.now(),
    });
    setBooking(false);
  };

  const handleCancelOwn = async () => {
    if (!club || !user) return;
    const ok = await confirm({ title: "Cancel booking?", message: "You'll free up your spot.", confirmLabel: "Cancel booking", destructive: true });
    if (!ok) return;
    await deleteDoc(doc(db, "clubs", club.id, "netSessions", sessionId, "bookings", user.uid));
  };

  const handleAdminCancel = async () => {
    if (!club || !cancelTarget || !cancelReason.trim()) return;
    setCancelling(true);
    await deleteDoc(doc(db, "clubs", club.id, "netSessions", sessionId, "bookings", cancelTarget.userId));
    await createPost(club.id, {
      authorId: user!.uid,
      authorName: membership!.displayName,
      authorRole: membership!.role,
      text: `🚫 Net booking cancelled for ${cancelTarget.displayName} on ${formatDate(date || "")} at ${formatTime(time || "")}.\nReason: ${cancelReason.trim()}`,
      visibility: "internal",
      clubName: club.name,
    });
    setCancelling(false);
    setCancelTarget(null);
    setCancelReason("");
    await alert({ title: "Booking cancelled", emoji: "🚫", message: `${cancelTarget.displayName} has been notified.` });
  };

  if (loading) return <View style={{ flex: 1, backgroundColor: colors.bg }}><SlotListSkeleton /></View>;

  return (
    <>
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Header>
          <DateTitle>{formatDate(date || "")}</DateTitle>
          <TimeText>{formatTime(time || "")} — {formatTime(`${parseInt((time || "9").split(":")[0]) + 1}:00`)}</TimeText>
          <SpotsText>{spotsLeft > 0 ? `${spotsLeft}/${lanes} spots available` : "Full"}</SpotsText>
        </Header>

        <Section>
          {!myBooking && spotsLeft > 0 && (
            <Pressable onPress={handleBook} disabled={booking} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
              <BookBtn>
                {booking ? <ActivityIndicator color="#fff" /> : <BookBtnText>Book your spot</BookBtnText>}
              </BookBtn>
            </Pressable>
          )}
          {myBooking && (
            <>
              <BookedBadge>
                <BookedBadgeText>✅ You're booked</BookedBadgeText>
              </BookedBadge>
              <Pressable onPress={handleCancelOwn}>
                <CancelLink>Cancel my booking</CancelLink>
              </Pressable>
            </>
          )}
          {!myBooking && spotsLeft <= 0 && (
            <FullBadge>
              <FullBadgeText>Session is full</FullBadgeText>
            </FullBadge>
          )}
        </Section>

        <Section>
          <SectionLabel>Booked ({bookings.length}/{lanes})</SectionLabel>
          {bookings.length > 0 ? (
            <Card>
              {bookings.map((b, i) => (
                <Row key={b.id} bordered={i < bookings.length - 1}>
                  <Avatar><AvatarText>{b.displayName?.[0]?.toUpperCase()}</AvatarText></Avatar>
                  <BookingName>{b.displayName}</BookingName>
                  {isAdmin && b.userId !== user?.uid && (
                    <Pressable onPress={() => setCancelTarget(b)}>
                      <AdminCancelBtn>Cancel</AdminCancelBtn>
                    </Pressable>
                  )}
                </Row>
              ))}
            </Card>
          ) : (
            <EmptyText>No bookings yet. Be the first!</EmptyText>
          )}
        </Section>

        <Spacer />
      </ContentWidth>
    </KeyboardAwareScroll>

    {/* Admin cancel with reason */}
    <Modal visible={!!cancelTarget} animationType="slide" transparent>
      <ModalContent onClose={() => { setCancelTarget(null); setCancelReason(""); }}>
        <ReasonSheet>
          <ReasonTitle>Cancel booking</ReasonTitle>
          <ReasonSub>Cancel {cancelTarget?.displayName}'s booking. Please provide a reason.</ReasonSub>
          <ReasonInput
            placeholder="e.g. Ground maintenance, weather..."
            placeholderTextColor={colors.inkMuted}
            value={cancelReason}
            onChangeText={setCancelReason}
            multiline
          />
          <Pressable onPress={handleAdminCancel} disabled={cancelling || !cancelReason.trim()} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
            <ReasonBtn disabled={!cancelReason.trim()}>
              {cancelling ? <ActivityIndicator color="#fff" /> : <ReasonBtnText>Cancel booking</ReasonBtnText>}
            </ReasonBtn>
          </Pressable>
          <Pressable onPress={() => { setCancelTarget(null); setCancelReason(""); }}>
            <ReasonCancelText>Keep booking</ReasonCancelText>
          </Pressable>
        </ReasonSheet>
      </ModalContent>
    </Modal>

    <ConfirmSheet {...confirmProps} />
    <AlertSheet {...alertProps} />
    </>
  );
}
